import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PageContainer from '../../components/layout/PageContainer/PageContainer';
import UserRegistrationChart from '../../components/admin/UserRegistrationChart';
import UsersTable from '../../components/admin/UsersTable';
import SearchAnalytics from '../../components/admin/SearchAnalytics';
import AIValidationAnalytics from '../../components/admin/AIValidationAnalytics';
import { useAuth } from '../../hooks/useAuth';
import Card from '../../components/common/Card/Card';

interface UserTableData {
  id: string;
  email: string;
  username: string;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
  search_count: number;
}

const Analytics: React.FC = () => {
  const { user, isAuthenticated, loading: authLoading, fetchCurrentUser } = useAuth();
  const navigate = useNavigate();
  const userFetchedRef = useRef(false);
  const redirectAttempted = useRef(false);
  
  const [users, setUsers] = useState<UserTableData[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(true);
  const [errorUsers, setErrorUsers] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && !userFetchedRef.current) {
      fetchCurrentUser();
      userFetchedRef.current = true;
    }
  }, [isAuthenticated, fetchCurrentUser]);

  useEffect(() => {
    if (!authLoading && !redirectAttempted.current) {
      if (!isAuthenticated) {
        navigate('/login');
      } else if (!user || user.is_admin !== true) {
        navigate('/dashboard');
      }
      redirectAttempted.current = true;
    }
  }, [user, navigate, isAuthenticated, authLoading]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        setErrorUsers(null);

        const apiUrl = process.env.REACT_APP_API_URL || '';
        const token = localStorage.getItem('auth_token');
        const headers: HeadersInit = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        };

        const response = await fetch(`${apiUrl}/api/admin/users`, { headers });

        if (!response.ok) {
          throw new Error(`Failed to fetch users: ${response.status}`);
        }

        const usersData = await response.json();
        setUsers(usersData);
      } catch (err) {
        setErrorUsers(err instanceof Error ? err.message : 'Failed to fetch users');
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, []);

  const location = useLocation();
  const isAnalyticsPage = location.pathname === '/admin/analytics';

  if (authLoading || (!isAuthenticated) || !user || user.is_admin !== true) {
    return (
      <PageContainer>
        <Card>
          <div className="flex justify-center items-center py-12">
            {authLoading ? (
              <span className="text-gray-600">Loading...</span>
            ) : !isAuthenticated ? (
              <span className="text-gray-600">Please log in to access this page</span>
            ) : (
              <span className="text-gray-600">You don't have permission to access the admin panel</span>
            )}
          </div>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="Admin - Analytics">
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8" aria-label="Tabs">
          <Link
            to="/admin"
            className={`${
              !isAnalyticsPage
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Overview
          </Link>
          <Link
            to="/admin/analytics"
            className={`${
              isAnalyticsPage
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Analytics
          </Link>
        </nav>
      </div>

      <div className="space-y-8">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-6">User Analytics</h2>
          
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-700 mb-4">Registration Analytics</h3>
            <UserRegistrationChart />
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-700 mb-4">All Users</h3>
            {loadingUsers ? (
              <p className="text-sm text-gray-600">Loading users...</p>
            ) : errorUsers ? (
              <p className="text-sm text-red-600">Error: {errorUsers}</p>
            ) : (
              <UsersTable users={users} />
            )}
          </div>
        </div>

        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-6">Search Analytics</h2>
          <SearchAnalytics />
        </div>

        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-6">AI Validation Analytics</h2>
          <AIValidationAnalytics />
        </div>
      </div>
    </PageContainer>
  );
};

export default Analytics;

